import { useState } from 'react';

export interface UseLocalStorageReturn<T> {
  value: T;
  setValue: (_value: T) => void;
  removeValue: () => void;
}

export function useLocalStorage<T>(
  key: string,
  defaultValue: T
): UseLocalStorageReturn<T> {
  const [storedValue, setStoredValue] = useState<T>(() => {
    try {
      if (typeof globalThis !== 'undefined' && globalThis.window) {
        const item = globalThis.window.localStorage.getItem(key);
        return item ? JSON.parse(item) : defaultValue;
      }
      return defaultValue;
    } catch (error) {
      // Error reading localStorage - return default value
      return defaultValue;
    }
  });

  const setValue = (newValue: T) => {
    try {
      setStoredValue(newValue);
      if (typeof globalThis !== 'undefined' && globalThis.window) {
        globalThis.window.localStorage.setItem(key, JSON.stringify(newValue));
      }
    } catch (error) {
      // Error setting localStorage - continue silently
    }
  };

  const removeValue = () => {
    try {
      setStoredValue(defaultValue);
      if (typeof globalThis !== 'undefined' && globalThis.window) {
        globalThis.window.localStorage.removeItem(key);
      }
    } catch (error) {
      // Error removing localStorage - continue silently
    }
  };

  return {
    value: storedValue,
    setValue,
    removeValue,
  };
}